import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import MainNavbar from "../components/navbar";
import { createAllResourcesList, loadFromLocalStorage } from "../utils";
import { activityLog } from "../objects/activityLog";
import { apiScopes } from "../authConfig";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import Swal from "sweetalert2";
import { errorsTypes } from "../errorTypes";
import { InteractionStatus } from "@azure/msal-browser";
import {
  Shimmer,
  ShimmerElementType,
  DefaultButton,
  Icon,
} from "@fluentui/react";
import { getSingleOwnedResource } from "../services/resource-manager";
import { m365group } from "../objects/m365group";
import { nonConnectedSite } from "../objects/spoSite";
import { resource } from "../objects/resource";
import { ResourceGrid } from "../components/resources-grid";
import { ChangeType } from "../enums";

const SingleWorkspace: React.FunctionComponent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { instance, inProgress, accounts } = useMsal();

  const [baseResourceList, setBaseResourceList] =
    useState<Array<resource> | null>(null);
  const [currentResourceList, setCurrentResourceList] = useState<
    Array<resource>
  >([]);
  const [resourceLoadComplete, toggleResourceLoadComplete] =
    useState<boolean>(false);
  const [fetchError, setFetchError] = useState<boolean>(false);

  // Get parameters from query string
  const resourceid = searchParams.get("resourceid");
  const isconnected = searchParams.get("isconnected");

  // Memoized parameter validation — same logic as original
  const isParamsValid = useMemo(() => {
    return !!(
      resourceid &&
      isconnected &&
      resourceid.trim() !== "" &&
      isconnected.trim() !== "" &&
      isconnected.length === 1
    );
  }, [resourceid, isconnected]);

  // Derived boolean — true only when isconnected is 'y' or 'Y'
  const isConnectedBool = useMemo(() => {
    return isconnected === "Y" || isconnected === "y";
  }, [isconnected]);

  // Redirect to root if parameters are invalid
  useEffect(() => {
    if (!isParamsValid) {
      navigate("/", { replace: true });
    }
  }, [isParamsValid, navigate]);

  // Auto-expand the resource group once the grid is rendered
  useEffect(() => {
    if (resourceLoadComplete && !fetchError && currentResourceList.length > 0) {
      const timer = setTimeout(() => {
        const expandBtn = document.getElementById("btn-expandResourceAny0");
        if (expandBtn) {
          expandBtn.click();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [resourceLoadComplete, fetchError, currentResourceList.length]);

  const getResourceLoadingShimmer = (): JSX.Element => {
    const shimmerRowsList = [];
    for (let index = 0; index < 10; index++) {
      shimmerRowsList.push(
        <Shimmer
          key={index}
          shimmerElements={[
            { type: ShimmerElementType.circle, height: 30 },
            { type: ShimmerElementType.gap, width: "1%", height: 30 },
            { type: ShimmerElementType.line, height: 30 },
          ]}
        />,
      );
    }
    return <div>{shimmerRowsList}</div>;
  };

  const refreshMainDataset = (
    _resourceID: string = "",
    _changeType: ChangeType,
    _value: any,
  ) => {
    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (!isParamsValid) return;

    async function fetchData() {
      try {
        const RMTAccessTokenRequest = {
          scopes: apiScopes.resourceManagementScopes,
          account: accounts[0],
        };

        const RMTAccessTokenResponse = await instance.acquireTokenSilent(
          RMTAccessTokenRequest,
        );

        const resourceList = await getSingleOwnedResource(
          RMTAccessTokenResponse.accessToken,
          resourceid as string,
          isConnectedBool,
        );

        // ✅ using isConnectedBool instead of the raw string
        if (isConnectedBool) {
          const m365GroupResult = resourceList as Array<m365group>;
          const resourceResult = createAllResourcesList([], m365GroupResult);
          setBaseResourceList(resourceResult);
          setCurrentResourceList(resourceResult);
        } else {
          const spoSiteResult = resourceList as Array<nonConnectedSite>;
          const resourceResult = createAllResourcesList(spoSiteResult, []);
          setBaseResourceList(resourceResult);
          setCurrentResourceList(resourceResult);
        }

        toggleResourceLoadComplete(true);
      } catch (ex) {
        toggleResourceLoadComplete(true);
        setFetchError(true);
        Swal.fire(
          errorsTypes.somethingWentWrong.errorTitle,
          errorsTypes.somethingWentWrong.errorSubtitle,
          "error",
        );
      }
    }

    if (baseResourceList === null && inProgress === InteractionStatus.None) {
      fetchData();
    }
  }, [
    accounts,
    baseResourceList,
    inProgress,
    instance,
    isParamsValid,
    isConnectedBool,
    resourceid,
    isconnected,
  ]);

  // Don't render anything while redirecting away from invalid params
  if (!isParamsValid) {
    return null;
  }

  // Throw a 404 Response so React Router renders ErrorPage via errorElement
  if (resourceLoadComplete && !fetchError && currentResourceList.length === 0) {
    throw new Response("Workspace not found", {
      status: 404,
      statusText: "Not Found",
    });
  }

  return (
    <div>
      <MainNavbar
        userName={window.CURRENT_USER_EMAIL}
        userActivity={loadFromLocalStorage<activityLog>("userActivityData")}
      />

      <div className="container-fluid responsive-padding">
        {/* Header row — back button + explanation */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            margin: "12px 0 20px 0",
          }}
        >
          <DefaultButton
            text="Back to My Workspaces"
            iconProps={{ iconName: "Back" }}
            onClick={() => navigate("/")}
            ariaLabel="Back to My Workspaces"
          />
          <span style={{ color: "#605e5c", fontSize: "16px" }}>
            <Icon
              iconName="Info"
              style={{ marginRight: "6px", verticalAlign: "middle" }}
            />
            You can visit <strong>My Workspaces</strong> to browse and manage
            all workspaces you own.
          </span>
        </div>

        <AuthenticatedTemplate>
          {/* Error state */}
          {fetchError && resourceLoadComplete && (
            <div className="alert alert-danger d-flex align-items-center justify-content-between mt-3">
              <span>Failed to load workspace resources.</span>
              <button className="btn btn-sm btn-danger" onClick={()=>{navigate("/", { replace: true });}}>
                Retry
              </button>
            </div>
          )}

          {/* Loading shimmer — only shown while loading */}
          {!resourceLoadComplete && getResourceLoadingShimmer()}

          {/* Resource grid — only shown when loading is done, no error, and list has items */}
          {resourceLoadComplete &&
            !fetchError &&
            currentResourceList.length > 0 && (
              <ResourceGrid
                ownedResources={currentResourceList}
                resourceType="Any"
                refreshDatasetFunction={refreshMainDataset}
              />
            )}
        </AuthenticatedTemplate>

        <UnauthenticatedTemplate>
          <div className="alert alert-warning mt-3" role="alert">
            You are not signed in. Please sign in to view this workspace.
          </div>
        </UnauthenticatedTemplate>
      </div>
    </div>
  );
};

export default SingleWorkspace;